import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {ToggleButtonGroup, ToggleButton, Typography, SvgIconProps} from '@mui/material'
import React, {FC, Key} from 'react'

export type ToggleConfig<T> = {
  type: T
  labelKey: string
  icon?: FC<SvgIconProps>
  dataTestId: string
}[]

type ToggleProps<T> = {
  config: ToggleConfig<T>
  value: T
  onChange: (value: T) => void
  t: (key: string) => string
  fullWidth?: boolean
  // true (legacy blue surface) keeps the unselected label white; false follows the text token
  isContrastMode?: boolean
}

export const Toggle = <T extends Key>({
  value,
  onChange,
  t,
  fullWidth,
  config,
  isContrastMode = true
}: ToggleProps<T>) => {
  const onValueChanged = (event: React.MouseEvent<HTMLElement>, plantView: T | null) => {
    if (plantView) {
      onChange(plantView)
    }
  }

  return (
    <ToggleButtonGroup
      color="primary"
      exclusive
      value={value}
      onChange={onValueChanged}
      aria-label={t('plantViewToggle.ariaLabelToggle')}
      sx={{
        border: 1,
        borderColor: customThemeConstants().palette.mediumInputBorder
      }}
      fullWidth={fullWidth}
    >
      {config.map(({type, labelKey, icon: Icon, dataTestId}) => (
        <ToggleButton
          sx={(theme) => ({
            '.MuiToggleButtonGroup-grouped&:not(.Mui-selected)': {
              color: isContrastMode ? theme.palette.common.white : theme.palette.text.primary,
              '&:hover': {
                color: theme.palette.text.primary
              }
            }
          })}
          key={type}
          value={type}
          data-test-id={dataTestId}
        >
          {Icon && <Icon />}
          <Typography variant="button" ml={Icon ? 1 : 0}>
            {t(labelKey)}
          </Typography>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}
